
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

const EmailConfirmation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [code, setCode] = useState('');
  const [email, setEmail] = useState('');
  const [showCodeInput, setShowCodeInput] = useState(false);

  useEffect(() => {
    const handleEmailConfirmation = async () => {
      // Check for tokens in URL hash (which is how Supabase redirects work)
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');
      const type = hashParams.get('type');

      // Also check traditional query params
      const token = searchParams.get('token');
      const queryType = searchParams.get('type');

      if ((accessToken && refreshToken && type === 'signup') || (token && queryType === 'signup')) {
        setIsLoading(true);
        try {
          if (accessToken && refreshToken) {
            // Handle hash-based auth tokens
            const { error } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken
            });

            if (error) {
              console.error('Session setup error:', error);
              toast.error('Email confirmation failed. Please try again.');
              setShowCodeInput(true);
            } else {
              toast.success('Email confirmed successfully! Welcome!');
              navigate('/');
            }
          } else if (token) {
            // Handle traditional token verification
            const { error } = await supabase.auth.verifyOtp({
              token_hash: token,
              type: 'signup'
            });

            if (error) {
              console.error('Email confirmation error:', error);
              toast.error('Email confirmation failed. Please try again.');
              setShowCodeInput(true);
            } else {
              toast.success('Email confirmed successfully! You can now sign in.');
              navigate('/');
            }
          }
        } catch (error) {
          console.error('Confirmation error:', error);
          toast.error('Something went wrong. Please try again.');
          setShowCodeInput(true);
        } finally {
          setIsLoading(false);
        }
      } else {
        setShowCodeInput(true);
      }
    };

    handleEmailConfirmation();
  }, [searchParams, navigate]);

  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !code) {
      toast.error('Please enter both email and confirmation code.');
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase.auth.verifyOtp({
        email,
        token: code,
        type: 'signup'
      });

      if (error) {
        toast.error(error.message);
      } else {
        toast.success('Email confirmed successfully! You can now sign in.');
        navigate('/');
      }
    } catch (error) {
      console.error('Code verification error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading && !showCodeInput) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p>Confirming your email...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Confirm Your Email</CardTitle>
          <CardDescription>
            Enter your email and the confirmation code we sent you.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleCodeSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
              />
            </div>
            
            <div>
              <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1">
                Confirmation Code
              </label>
              <Input
                id="code"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Enter 6-digit code"
                maxLength={6}
                required
              />
            </div>

            <Button 
              type="submit" 
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? 'Confirming...' : 'Confirm Email'}
            </Button>
          </form>
          
          <div className="mt-4 text-center">
            <Button 
              variant="link" 
              onClick={() => navigate('/')}
              className="text-sm"
            >
              Back to Home
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailConfirmation;
